import type {
  PublicFilePasswordEntry,
  PublicFilesPasswordsSlice,
} from '../../interfaces/PublicFilesPasswordsSlice';

const TTL_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Zustand slice for public file passwords (in-memory only, not persisted).
 * When the user enters the correct password on FilePublicLink or Viewer, we store
 * token → password so that returning to the same link or viewer within 60 min
 * does not require re-entering the password.
 */
export const createPublicFilesPasswordsSlice = (
  set: (fn: (state: PublicFilesPasswordsSlice) => Partial<PublicFilesPasswordsSlice>) => void,
  get: () => PublicFilesPasswordsSlice,
): PublicFilesPasswordsSlice => ({
  publicFilePasswords: [],

  setPublicFilePassword: (token: string, password: string) => {
    const expiresAt = Date.now() + TTL_MS;
    set((state) => {
      const filtered = state.publicFilePasswords.filter((e) => e.token !== token);
      return {
        publicFilePasswords: [...filtered, { token, password, expiresAt }],
      };
    });
  },

  getPublicFilePassword: (token: string) => {
    const state = get();
    const entry = state.publicFilePasswords.find((e: PublicFilePasswordEntry) => e.token === token);
    if (!entry) return null;

    const now = Date.now();
    if (entry.expiresAt < now) {
      set((s) => ({
        publicFilePasswords: s.publicFilePasswords.filter((e) => e.token !== token),
      }));
      return null;
    }
    return entry.password;
  },

  removePublicFilePassword: (token: string) => {
    set((state) => ({
      publicFilePasswords: state.publicFilePasswords.filter((e) => e.token !== token),
    }));
  },
});
